import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../components/autentication-context.component';
import { getProfile, update } from '../../services/profile.service';
import { iProfile } from '../interfaces/profile.interface';
import { Container, Alert, Button } from 'react-bootstrap';
import { SpinnerComponent } from '../components/spinner.component';
import ProfileDetails from '../components/profile-details.component';
import ProfileEdit from '../components/profile-edit.component';
import { LogoutProps } from '../interfaces/logout.interface';
import { handleApiError } from '../../services/error.service';

const Profile: React.FC<LogoutProps> = ({ handleLogout }) => {
  const { employeeId, token, updateToken } = useAuth();
  const [profile, setProfile] = useState<iProfile | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!employeeId || !token) return;
    setLoading(true);

    try {
      const fetchedProfile = await getProfile(employeeId, token, updateToken);
      setProfile(fetchedProfile);
    } catch (error) {
      handleApiError({ error, handleLogout, setNotification });
    } finally {
      setLoading(false);
    }
  }, [employeeId, token, updateToken, handleLogout]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSaveProfile = useCallback(
    async (updatedProfile: iProfile) => {
      setLoading(true);
      try {
        const response = await update(employeeId!, updatedProfile, token!, updateToken);
        if (!response) {
          setNotification({ type: 'error', message: 'Failed to update profile.' });
          return;
        }
        setProfile(updatedProfile);
        setNotification({ type: 'success', message: 'Profile updated successfully.' });
        setIsEditing(false);
      } catch (error) {
        handleApiError({ error, handleLogout, setNotification });
      } finally {
        setLoading(false);
      }
    },
    [employeeId, token, updateToken, handleLogout]
  );

  return (
    <Container className="mt-5">
      {loading && <SpinnerComponent />}

      {!loading && notification && (
        <Alert variant={notification.type} className="text-center">
          {notification.message}
        </Alert>
      )}

      {!loading && profile && (
        isEditing ? (
          <ProfileEdit profile={profile} onSave={handleSaveProfile} onCancel={() => setIsEditing(false)} />
        ) : (
          <>
            <div className="text-center mt-3">
              <Button onClick={() => setIsEditing(true)} className="greenButton py-3 btn-lg mb-4">
                Edit Profile
                <i className="bi bi-pencil-square ms-2"></i>
              </Button>
            </div>
            <ProfileDetails profile={profile} />
          </>
        )
      )}
    </Container>
  );
};

export default Profile;